import React, { useMemo, useState } from "react";
import { Star, Loader2 } from "lucide-react";
import logo from "./ui/images/logowhite.jpeg";

const VERCEL_API_BASE = process.env.REACT_APP_API_BASE;

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

// --- Plan catalog (must match the `plan` ids in the backend) ---
const PLANS = [
  {
    id: "membership",
    title: "CBA Membership",
    price: "$160",
    period: "monthly",
    featured: true,
    details: {
      heading: "1-Hour Structured Group Class",
      days: "Monday – Thursday",
      scheduleNote:
        'Class schedules vary by age group. Refer to the "Classes" tab for specific class times.',
      fridayLabel: "Friday",
      fridaySub: "Open Gym & Sparring Day",
      fridayTime: "5:00 PM – 9:00 PM",
    },
  },
  {
    id: "open_gym",
    title: "Open Gym",
    price: "$65",
    period: "monthly",
    featured: false,
    details: {
      description:
        "Access to the gym during regular business hours for self-training.",
      days: "Monday – Friday",
      time: "5:00 PM – 9:00 PM",
    },
  },
  {
    id: "day_pass",
    title: "CBA Day Pass",
    price: "$20",
    period: "day",
    featured: false,
    details: {
      description: "One 1-hour structured group class or open gym pass.",
      note: "Recommended for visitors",
    },
  },
  {
    id: "one_on_one",
    title: "1-on-1 Training",
    price: "$50",
    period: "hr",
    featured: false,
    details: {
      description:
        "Private training with a dedicated coach, focused on your goals.",
      bullets: ["Choose your coach", "Choose your schedule"],
    },
  },
];

/* -------------------------------------------------------------------------- */
/* Plan-specific body content (matches the reference flyer)                    */
/* -------------------------------------------------------------------------- */

const TimePill = ({ children }) => (
  <span className="inline-block rounded-md bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
    {children}
  </span>
);

const PlanDetails = ({ plan }) => {
  const d = plan.details;

  if (plan.id === "membership") {
    return (
      <div className="text-center">
        <p className="text-sm font-normal uppercase tracking-wide text-neutral-900">
          {d.heading}
        </p>
        <p className="text-sm font-extrabold uppercase tracking-wide text-neutral-900">
          {d.days}
        </p>
        <p className="mb-4 mt-2 text-xs font-normal normal-case leading-relaxed text-neutral-600">
          ({d.scheduleNote})
        </p>

        <div className="my-4 border-t border-neutral-300" />

        <p className="text-sm font-extrabold uppercase tracking-wide text-neutral-900">
          {d.fridayLabel}
        </p>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-700">
          {d.fridaySub}
        </p>
        <TimePill>{d.fridayTime}</TimePill>
      </div>
    );
  }

  if (plan.id === "open_gym") {
    return (
      <div className="text-center">
        <p className="mb-6 text-sm font-semibold uppercase leading-relaxed tracking-wide text-neutral-800">
          {d.description}
        </p>
        <p className="mb-3 text-sm font-extrabold uppercase tracking-wide text-red-600">
          {d.days}
        </p>
        <TimePill>{d.time}</TimePill>
      </div>
    );
  }

  if (plan.id === "day_pass") {
    return (
      <div className="text-center">
        <p className="mb-6 text-sm font-semibold uppercase leading-relaxed tracking-wide text-neutral-800">
          {d.description}
        </p>
        <div className="flex items-center justify-center gap-2">
          <Star size={14} className="fill-red-600 text-red-600" />
          <span className="text-xs font-bold uppercase tracking-wide text-neutral-900">
            {d.note}
          </span>
          <Star size={14} className="fill-red-600 text-red-600" />
        </div>
      </div>
    );
  }

  // one_on_one
  return (
    <div className="text-center">
      <p className="mb-6 text-sm font-semibold uppercase leading-relaxed tracking-wide text-neutral-800">
        {d.description}
      </p>
      <ul className="mx-auto inline-block space-y-2 text-left">
        {d.bullets.map((b) => (
          <li key={b} className="flex items-center gap-2">
            <Star size={14} className="fill-red-600 text-red-600 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wide text-neutral-900">
              {b}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Single plan card — self-contained form + Stripe checkout                    */
/* -------------------------------------------------------------------------- */

const PlanCard = ({ plan }) => {
  const [memberName, setMemberName] = useState("");
  const [memberEmail, setMemberEmail] = useState("");
  const [errors, setErrors] = useState({ memberName: "", memberEmail: "" });
  const [loading, setLoading] = useState(false);

  const validateAll = () => {
    const next = { memberName: "", memberEmail: "" };

    if (!memberName.trim()) next.memberName = "Member name is required.";

    const emailTrim = memberEmail.trim();
    if (!emailTrim) next.memberEmail = "Email is required.";
    else if (!isValidEmail(emailTrim)) next.memberEmail = "Enter a valid email.";

    setErrors(next);
    return !next.memberName && !next.memberEmail;
  };

  const isFormValid = useMemo(
    () => memberName.trim().length > 0 && isValidEmail(memberEmail.trim()),
    [memberName, memberEmail]
  );

  const handleGetStartedClick = async () => {
    if (!validateAll() || loading) return;

    setLoading(true);
    try {
      const res = await fetch(`${VERCEL_API_BASE}/api/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan: plan.id,
          memberEmail: memberEmail.trim(),
          memberName: memberName.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error("Checkout session error:", data);
        alert(data?.error || "Something went wrong.");
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        alert("No checkout URL returned.");
      }
    } catch (err) {
      console.error(err);
      alert("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-2xl border-2 p-2.5 shadow-lg transition-transform duration-300 hover:-translate-y-1 ${
        plan.featured
          ? "border-red-600 bg-red-600"
          : "border-white/15 bg-neutral-900"
      }`}
    >
      {/* Title header */}
      <div className="px-3 py-3 text-center">
        <h3 className="text-2xl font-extrabold uppercase leading-none tracking-tight text-white">
          {plan.title}
        </h3>
      </div>

      {/* Light body panel */}
      <div className="flex flex-1 flex-col rounded-xl bg-neutral-50 px-5 py-6">
        {/* Price */}
        <div className="mb-4 flex items-end justify-center gap-1">
          <span className="text-5xl font-extrabold leading-none text-red-600">
            {plan.price}
          </span>
          <span className="mb-1 text-sm font-bold uppercase text-neutral-600">
            /{plan.period}
          </span>
        </div>

        <div className="mb-5 border-t border-neutral-300" />

        {/* Plan-specific content */}
        <PlanDetails plan={plan} />

        {/* Member form + checkout (pushed to the bottom for card alignment) */}
        <div className="mt-auto pt-6">
          <input
            type="text"
            placeholder="Member's Name (First & Last)"
            className="mb-1 w-full rounded-md border border-neutral-300 bg-white p-2.5 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-red-600"
            value={memberName}
            onChange={(e) => {
              setMemberName(e.target.value);
              if (errors.memberName)
                setErrors((p) => ({ ...p, memberName: "" }));
            }}
            onBlur={validateAll}
            required
          />
          {errors.memberName ? (
            <p className="mb-2 text-xs text-red-600">{errors.memberName}</p>
          ) : (
            <div className="mb-2" />
          )}

          <input
            type="email"
            placeholder="Email"
            className="mb-1 w-full rounded-md border border-neutral-300 bg-white p-2.5 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-red-600"
            value={memberEmail}
            onChange={(e) => {
              setMemberEmail(e.target.value);
              if (errors.memberEmail)
                setErrors((p) => ({ ...p, memberEmail: "" }));
            }}
            onBlur={validateAll}
            required
          />
          {errors.memberEmail ? (
            <p className="mb-3 text-xs text-red-600">{errors.memberEmail}</p>
          ) : (
            <div className="mb-3" />
          )}

          <button
            className={`flex w-full items-center justify-center gap-2 rounded-md py-3 px-6 text-sm font-bold uppercase tracking-wide transition-colors duration-300 ${
              isFormValid && !loading
                ? "bg-red-600 text-white hover:bg-red-700"
                : "cursor-not-allowed bg-red-600/50 text-white/70"
            }`}
            onClick={handleGetStartedClick}
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Processing…
              </>
            ) : (
              "Get Started"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Section                                                                     */
/* -------------------------------------------------------------------------- */

const MembershipSection = () => {
  return (
    <section
      id="membership"
      className="relative overflow-hidden bg-black py-20"
      style={{
        backgroundImage:
          "radial-gradient(1200px 500px at 15% 0%, rgba(220,38,38,0.18), transparent 60%), radial-gradient(1000px 500px at 90% 20%, rgba(220,38,38,0.10), transparent 55%)",
      }}
    >
      <div className="container relative mx-auto px-4">
        {/* Header */}
        <div className="mb-14 flex flex-col items-center gap-6 text-center lg:flex-row lg:justify-center lg:text-left">
          <img
            src={logo}
            alt="Clara Boxing Academy"
            className="h-20 w-auto flex-shrink-0 md:h-24"
          />
          <div>
            <h2 className="text-3xl font-extrabold uppercase leading-tight tracking-tight text-white md:text-5xl">
              Ready to <span className="text-red-600">Become a Member?</span>
            </h2>
            <p className="mt-2 text-base font-semibold uppercase tracking-wide text-gray-300 md:text-xl">
              Choose the training option that’s right for you.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default MembershipSection;
